/**
 * Simultaneous multi-forward destinations helpers.
 *
 * Routes store both:
 *   - primaryDestinations / offHoursDestinations: string[] (E.164 or "none")
 *   - primaryNumber / offHoursNumber: first destination (compat with older readers)
 */

/** Max simultaneous destinations per forward slot. */
export const MAX_FORWARD_DESTINATIONS = 5;

/**
 * Normalize destinations[] + optional scalar number into a clean phone list.
 * Older routes only have the scalar — treat that as a one-element list.
 */
export function normalizeDestinations(destinations, fallbackNumber) {
  if (Array.isArray(destinations) && destinations.length > 0) {
    return destinations
      .filter((s) => s != null && String(s).trim() !== "")
      .map((s) => String(s).trim());
  }
  if (fallbackNumber != null && String(fallbackNumber).trim() !== "") {
    return [String(fallbackNumber).trim()];
  }
  return [];
}

/**
 * Keep scalar primaryNumber/offHoursNumber in sync with destinations[0].
 * @returns {{ destinations: string[]|null, primary: string|null }}
 */
export function syncDestinationsForSave(destinations, fallbackNumber) {
  const list = normalizeDestinations(destinations, fallbackNumber);
  return {
    destinations: list.length > 0 ? list : null,
    primary: list.length > 0 ? list[0] : null,
  };
}

/** True when the list has at least one real dial target (not empty / not only "none"). */
export function hasDialableDestination(destinations, fallbackNumber) {
  const list = normalizeDestinations(destinations, fallbackNumber);
  if (list.length === 0) return false;
  if (list.length === 1 && list[0].toLowerCase() === "none") return false;
  return list.some((d) => d && d.toLowerCase() !== "none");
}

/** Dialable phones only (excludes empty / "none"). */
export function dialableDestinationCount(destinations, fallbackNumber) {
  return normalizeDestinations(destinations, fallbackNumber).filter(
    (d) => d && String(d).toLowerCase() !== "none"
  ).length;
}

/**
 * When simple forward crosses from 1 → 2+ simultaneous destinations, auto-enable
 * Twilanswer VM fallback + call approval so a personal cellphone VM can't "win"
 * the ring race. Only fires on the crossing edge — later edits keep user choices.
 *
 * @returns {{ enableVmFallback: boolean, enableCallApproval: boolean }}
 */
export function getMultiForwardProtectionUpdates({
  previousCount,
  nextCount,
  isSimpleForward,
}) {
  if (!isSimpleForward) {
    return { enableVmFallback: false, enableCallApproval: false };
  }
  const crossingToMulti = previousCount < 2 && nextCount >= 2;
  return {
    enableVmFallback: crossingToMulti,
    enableCallApproval: crossingToMulti,
  };
}
